import React, { useEffect } from 'react';
import { 
  Brain, Code, Network, Shield,
  Check, Lightbulb, Terminal
} from 'lucide-react';

const JobRequirement = ({ children }) => (
  <div className="flex items-start gap-3 group">
    <div className="p-1 rounded bg-[#017D7E]/10 group-hover:bg-[#017D7E]/20 transition-colors">
      <Check className="w-4 h-4 text-[#017D7E]" />
    </div>
    <span className="text-white/70 group-hover:text-white transition-colors">{children}</span>
  </div>
);

const TechBadge = ({ children }) => (
  <span className="px-4 py-1.5 rounded-full bg-[#017D7E]/10 text-[#017D7E] text-sm border border-[#017D7E]/20">
    {children}
  </span>
);

const ProjectCard = ({ icon: Icon, title, items }) => (
  <div className="bg-[#017D7E]/5 rounded-lg p-6 border border-[#017D7E]/10 space-y-4">
    <h3 className="font-medium text-[#017D7E] text-lg">{title}</h3>
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-3">
          <Icon className="w-5 h-5 text-[#017D7E] mt-1" />
          <span className="text-white/70">{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const InternshipPage = () => {
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  return (
    <div className="min-h-screen text-white relative pt-20 pb-12">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-6">
              Stage R&D
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                IA & Blockchain Finance
              </span>
            </h1>

            <div className="flex flex-wrap gap-4 mb-6 text-white/70">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-[#017D7E]" />
                <span>100% Remote</span>
              </div>
              <div className="flex items-center gap-2">
                <Terminal className="w-5 h-5 text-[#017D7E]" />
                <span>Synchro hebdo à Casablanca</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#017D7E]" />
                <span>Stage - 6 mois</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <TechBadge>Python</TechBadge>
              <TechBadge>TensorFlow</TechBadge>
              <TechBadge>React</TechBadge>
              <TechBadge>Blockchain</TechBadge>
              <TechBadge>ML</TechBadge>
            </div>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-bold mb-4">Mission R&D</h2>
                <p className="text-white/70 mb-6">
                  Participez à l'innovation financière en rejoignant notre équipe R&D. 
                  Développez des solutions combinant IA et blockchain pour transformer les services financiers africains.
                </p>
                <div className="space-y-4">
                  <JobRequirement>
                    Développement de modèles IA pour l'analyse financière
                  </JobRequirement>
                  <JobRequirement>
                    Implémentation de smart contracts 
                  </JobRequirement>
                  <JobRequirement>
                    Création d'interfaces de visualisation
                  </JobRequirement>
                  <JobRequirement>
                    R&D en finance augmentée
                  </JobRequirement>
                </div>
              </section>

              <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-bold mb-4">Projets R&D</h2>
                <div className="space-y-4">
                  <ProjectCard
                    icon={Lightbulb}
                    title="IA & Finance"
                    items={[
                      "Prédiction des risques financiers",
                      "Détection de fraudes avancée"
                    ]}
                  />
                  <ProjectCard
                    icon={Network}
                    title="Solutions Blockchain"
                    items={[
                      "Smart contracts pour services financiers",
                      "Sécurisation des transactions"
                    ]}
                  />
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-lg font-bold mb-4">Profil recherché</h2>
                <div className="space-y-4">
                  <JobRequirement>
                    Bac+4/5 en informatique ou data science
                  </JobRequirement>
                  <JobRequirement>
                    Python et JavaScript
                  </JobRequirement>
                  <JobRequirement>
                    ML/DL fondamentaux
                  </JobRequirement>
                  <JobRequirement>
                    Intérêt blockchain/DeFi
                  </JobRequirement>
                </div>
              </section>

              <div className="sticky top-6">
                <div className="bg-gradient-to-r from-[#017D7E]/10 to-[#00A99D]/10 rounded-lg p-6 border border-[#017D7E]/20">
                  <h3 className="text-lg font-medium text-white mb-3">Pour postuler</h3>
                  <p className="text-white/70 mb-4">
                    Envoyez votre CV à :
                  </p>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <p className="font-mono text-sm text-[#017D7E]">contact@kg-technologies.fr</p>
                    <p className="text-sm text-white/50 mt-2">Objet : Candidature Stage R&D</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternshipPage;